import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import "flatpickr/dist/flatpickr.css";
import * as Yup from "yup";
import { addLiability, getLiabilitiesByPatrimonialStateId } from "helpers/fakebackend_helper";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../../context/LoaderContext";

const Liability = forwardRef(
  (
    {
      onSuccess,
      patrimonialStateId,
    }: { onSuccess?: () => void; patrimonialStateId?: string },
    ref
  ) => {
    const [showLiabilityForm, setShowLiabilityForm] = useState(false);
    const [liabilityList, setLiabilityList] = useState<any[]>([]);
    const { showLoader, hideLoader } = useLoader();

    const handleAddNewLiability = () => {
      setShowLiabilityForm(true);
    };

    const handleBackToList = () => {
      setShowLiabilityForm(false);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        validation.handleSubmit();
      },
      resetForm: () => {
        validation.resetForm();
        setLiabilityList([]);
      },
    }));

    /**
     * Fetch Liabilities by patrimonialStateId
     */
    const fetchLiabilities = async () => {
      if (!patrimonialStateId) return;

      const params = {patrimonialStateId}

      showLoader();
      try {
        const response = await getLiabilitiesByPatrimonialStateId(params);
        const liabilities = response.data._embedded.liabilities || [];
        setLiabilityList(liabilities);
        
        // Notificar al componente padre que los datos han cambiado
        onSuccess?.();
      } catch (error) {
        toast.error("Error al obtener los gastos: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    };

    /**
     * Effect to fetch liabilities when patrimonialStateId changes
     */
    useEffect(() => {
      fetchLiabilities();
    }, [patrimonialStateId]);

    const linkifyEntity = (entityName: string, id: number) => {
      return `/${entityName}s/${id}`;
    };

    const validation: any = useFormik({
      enableReinitialize: true,
      initialValues: {
        name: "",
        value: 0,
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Nombre es requerido"),
        value: Yup.number().required("Valor es requerido"),
      }),
      onSubmit: async (values: any) => {
        if (!patrimonialStateId) {
          toast.error("No se ha proporcionado el ID del estado patrimonial");
          return;
        }

        showLoader();
        try {
          const liabilityData = {
            patrimonialStatus: linkifyEntity("patrimonialStatus", Number(patrimonialStateId)),
            name: values.name,
            value: values.value,
          };

          const response = await addLiability(liabilityData);
          toast.success("Gasto creado correctamente");
          
          // Refresh the liability list after adding
          await fetchLiabilities();

          setShowLiabilityForm(false);
          validation.resetForm();
        } catch (error) {
          toast.error("Error al crear el gasto" + error);
        } finally {
          hideLoader();
        }
      },
    });

    // Calcular el total de liabilities para mostrar
    const totalLiabilities = liabilityList.reduce((sum, liability) => sum + (liability.value || 0), 0);

    const columns = [
      {
        header: "Nombre",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Valor",
        accessorKey: "value",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          return `Q${row.original.value.toLocaleString()}`;
        },
      },
    ];

    return (
      <React.Fragment>
        {showLiabilityForm ? (
          <>
            <h5 className="mb-3">Agregar Gasto</h5>
            <form onSubmit={validation.handleSubmit}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-input border-slate-200"
                    placeholder="Ingrese nombre"
                    value={validation.values.name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <div className="text-red-500">{validation.errors.name}</div>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="value" className="inline-block mb-2 text-base font-medium">
                    Valor <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="value"
                    className="form-input border-slate-200"
                    placeholder="Ingrese valor"
                    value={validation.values.value}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                  />
                  {validation.touched.value && validation.errors.value ? (
                    <div className="text-red-500">{validation.errors.value}</div>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={handleBackToList} className="btn bg-red-500 text-white">
                  Cancelar
                </button>
                <button type="submit" className="btn bg-green-500 text-white">
                  Guardar
                </button>
              </div>
            </form>
          </>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Gastos</h2>
                {liabilityList.length > 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    Total: Q{totalLiabilities.toLocaleString()}
                  </p>
                )}
              </div>
              <button
                className="btn bg-blue-500 text-white mt-4"
                onClick={handleAddNewLiability}
              >
                Agregar Gasto
              </button>
            </div>

            {liabilityList.length === 0 ? (
              <p className="text-gray-500">No se han agregado gastos aún.</p>
            ) : (
              <TableContainer
                isPagination={true}
                isGlobalFilter={false}
                columns={columns}
                data={liabilityList}
                customPageSize={5}
                divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                theadclassName="border-b border-slate-200 dark:border-zink-500"
                trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
          )}
              </div>
        )}
      </React.Fragment>
    );
  }
);

export default Liability;
